import { motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import { useStatusConfig } from "./findings-constants";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingLike {
  status: string;
}

interface FindingsHeatmapStripProps {
  findings: FindingLike[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingsHeatmapStrip({ findings }: FindingsHeatmapStripProps) {
  const { t } = useTranslation("audits");
  const statusConfig = useStatusConfig();
  const total = findings.length;
  const resolvedCount = findings.filter((f) => f.status === "resolved").length;
  const openCount = total - resolvedCount;
  const resolvedPct = total > 0 ? (resolvedCount / total) * 100 : 0;

  if (total === 0) return null;

  const resolvedColor = statusConfig.resolved.text;
  const openColor = statusConfig.open.text;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.snappy}
    >
      {/* Two-tone bar: resolved (left) + open (right) */}
      <div className="flex h-2.5 overflow-hidden rounded-full">
        {/* Resolved segment */}
        {resolvedCount > 0 && (
          <motion.div
            className="h-full"
            style={{ backgroundColor: resolvedColor }}
            initial={{ width: 0 }}
            animate={{ width: `${resolvedPct}%` }}
            transition={{ ...springs.smooth, delay: 0.2 }}
          />
        )}

        {/* Open segment — fills remaining space */}
        {openCount > 0 && (
          <div
            className="h-full flex-1"
            style={{ backgroundColor: `${openColor}30` }}
          />
        )}
      </div>

      {/* Label below bar */}
      <div className="mt-1.5 flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: resolvedColor }}
          />
          <span className="font-semibold" style={{ color: resolvedColor }}>
            {resolvedCount}
          </span>
          <span className="text-secondary">
            {t("findings.heatmap.resolvedLabel", {
              defaultValue: "Resolved",
            })}
          </span>
        </span>

        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: openColor }}
          />
          <span className="font-semibold" style={{ color: openColor }}>
            {openCount}
          </span>
          <span className="text-secondary">
            {t("findings.heatmap.openLabel", {
              defaultValue: "Open",
            })}
          </span>
        </span>
      </div>
    </motion.div>
  );
}
